import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';
import { queryKeys } from '../utils/queryKeys';
import _ from 'lodash';

const CARDS_PER_GROUP = 5;

const fetchTrendingCards = async (categoryId) => {
  try {
    // Primero obtenemos las 20 tarjetas con mayor rank general
    const { data: topRankedCards, error: rankError } = await supabase
      .from("cards")
      .select("id")
      .order("rank", { ascending: false })
      .limit(20);

    if (rankError) throw rankError;
    const topRankedIds = topRankedCards.map(card => card.id);

    // Luego obtenemos las tarjetas de la categoría específica
    let query = supabase
      .from("cards")
      .select(`
        id,
        companyname,
        rank,
        category_id
      `)
      .order("rank", { ascending: false });

    // Si no es "All" (id: 9), filtrar por categoría
    if (categoryId !== 9) {
      query = query.eq("category_id", categoryId);
    }

    const { data: categoryCards, error: categoryError } = await query;
    if (categoryError) throw categoryError;

    // Filtramos para obtener solo las tarjetas que están en el top 20 general
    return categoryCards.filter(card => topRankedIds.includes(card.id));
  } catch (error) {
    throw error;
  }
};

export function useTrendingSubCategories({
  componentSize = "Desktop 1440",
  selectedCategoryId = 9
}) {
  const { data: cards = [], isLoading, error } = useQuery({
    queryKey: queryKeys.cards.trending(selectedCategoryId),
    queryFn: () => fetchTrendingCards(selectedCategoryId),
    staleTime: 1000 * 60 * 5, // 5 minutos antes de considerar datos stale
    cacheTime: 1000 * 60 * 30, // Mantener en caché por 30 minutos
    refetchOnMount: false, // No refetch automático al montar
    refetchOnWindowFocus: false, // No refetch al focus
    refetchOnReconnect: false // No refetch al reconectar
  });

  // Procesar cards y organizar en grupos
  const processedData = React.useMemo(() => {
    // Agrupar cards en grupos de 5
    const cardGroups = _.chunk(cards, CARDS_PER_GROUP);

    // Obtener ancho del contenedor según tamaño
    const getContainerWidth = () => {
      switch (componentSize) {
        case "Phone":
          return 362.4;
        case "Tablet":
          return 626;
        case "Desktop 1200":
          return 1057;
        case "Desktop 1440":
        default:
          return 1210;
      }
    };

    return {
      cardGroups,
      containerWidth: getContainerWidth()
    };
  }, [cards, componentSize]);

  return {
    cardGroups: processedData.cardGroups,
    containerWidth: processedData.containerWidth,
    isLoading,
    error,
    // Estados adicionales para mejor control
    isEmpty: cards.length === 0,
    hasError: !!error
  };
}